import React, { useMemo } from 'react';

import useScreen from '@lyearn/core/hooks/useScreen';
import { useTranslation } from '@lyearn/i18n';
import { cn, css } from '@lyearn/style';
import { Input, RenderInputParams, Select, Stack, Typography } from '@lyearn/ui';
import { renderMediumOptionLabel } from '@/components/Form/components/Select/optionLabelRenderer';
import { FormSelectOption } from '@/components/Form/components/Select/types';
import useNavigation from '@/hooks/useNavigation';
import allRoutes from '@/pages/corporate/routes/contents';
import { articleDetailRoute } from '@/pages/corporate/routes/contents/articles';
import { courseDetailRoute } from '@/pages/corporate/routes/contents/course';
import { pathDetailRoute } from '@/pages/corporate/routes/contents/path';
import { quizDetailRoute } from '@/pages/corporate/routes/contents/quiz';
import { useAdminRoute } from '@/pages/corporate/routes/hooks/useAdminRoutes';
import { LanguageConfig, LanguageEnum, ResourceStatusEnum, ResourceTypeEnum } from '@/types/schema';

import SelectLanguageMobile from './SelectLanguageMobile';
interface SelectLanguageProps {
  placeholder?: string;
  languageConfigs: LanguageConfig[];
  languageType: LanguageEnum;
  id: string;
  resourceType: ResourceTypeEnum;
  localizedContentsMap?: Map<string, ResourceStatusEnum>;
  className?: string;
}

export const inputClasses = css`
  .MuiOutlinedInput-root.MuiAutocomplete-inputRoot {
    padding-inline-end: 0.4rem !important;
  }
`;

const RenderValue = <T,>({ Icon, label }: FormSelectOption<T>) => (
  <Stack alignItems="center" className="flex-1" gapX={6}>
    <Typography className="truncate" color="text-text-secondary" variant="body-short-02">
      Language : {label}
    </Typography>
  </Stack>
);
const renderInput = (params: RenderInputParams) => {
  const { inputProps = {}, ...rest } = params;

  const value = 'Language: ' + params.inputProps?.value;
  return (
    <Input readOnly={true} {...rest} endAdornment={<></>} inputProps={{ ...inputProps, value }} />
  );
};

const SelectLanguage: React.FC<SelectLanguageProps> = ({
  placeholder,
  languageConfigs,
  languageType,
  id,
  resourceType,
  localizedContentsMap,
  className,
}) => {
  const { t } = useTranslation();
  const navigation = useNavigation();
  const isAdminRoute = useAdminRoute();
  const { isMobile } = useScreen();

  const onChange = (value: any, option: any) => {
    let routes = courseDetailRoute(option?.contentId!);
    switch (resourceType) {
      case ResourceTypeEnum.Quiz:
        if (
          localizedContentsMap?.get(option?.contentId!) == ResourceStatusEnum.Draft &&
          isAdminRoute
        ) {
          const quizEditor = allRoutes.QuizEditor(option?.contentId!);
          navigation.replace(quizEditor, { appendQuery: true });
          return;
        } else {
          routes = quizDetailRoute(option?.contentId!);
        }
        break;
      case ResourceTypeEnum.Article:
        routes = articleDetailRoute(option?.contentId!);
        break;
      case ResourceTypeEnum.Path:
        routes = pathDetailRoute(option?.contentId!);
    }
    const { admin, learner } = routes;

    const courseDetailsRoute = isAdminRoute ? admin : learner;
    navigation.replace(courseDetailsRoute, { appendQuery: true });
  };

  const allLanguagesMap = useMemo(() => {
    const allLanguagesMap: { [key: string]: string } = {
      GUJARATI: 'Gujarati',
      KANNADA: 'Kannada',
      MALAYALAM: 'Malayalam',
    };
    Object.entries(LanguageEnum).forEach(([key, value]) => (allLanguagesMap[value] = key));
    return allLanguagesMap;
  }, []);

  const getOptionLabel = React.useCallback((item: any) => item.label, [languageConfigs]);
  const options = useMemo(() => {
    if (!languageType) {
      const englisLanguageConfig = languageConfigs.find(
        (languageConfig: any) => languageConfig.languageType == LanguageEnum.English,
      );
      if (!englisLanguageConfig) {
        languageConfigs.push({ languageType: LanguageEnum.English, contentId: id });
      }
    }
    const options = languageConfigs?.map((languageConfig: any) => ({
      value: languageConfig.languageType,
      label: allLanguagesMap[languageConfig.languageType],
      contentId: languageConfig.contentId,
    }));

    return options;
  }, [languageConfigs, languageType]);

  const currentLanguage = !!languageType ? languageType : LanguageEnum.English;

  if (isMobile) {
    return (
      <SelectLanguageMobile
        id={id}
        languageConfigs={languageConfigs}
        languageType={languageType}
        localizedContentsMap={localizedContentsMap}
        placeholder={placeholder}
        resourceType={resourceType}
      />
    );
  }

  return (
    <Select
      disableClearable
      className={cn('w-[18rem] xs:ml-8', className, inputClasses)}
      clearIcon={null}
      disabled={false}
      getOptionLabel={getOptionLabel}
      optionLabelRenderer={renderMediumOptionLabel}
      options={options}
      placeholder={placeholder ?? t('Common:Actions.ChooseLanguage')}
      popupIcon={null}
      renderInput={renderInput}
      renderValue={RenderValue}
      size="medium"
      value={currentLanguage}
      onChange={onChange}
    />
  );
};

export default SelectLanguage;
